import React from "react";
import { api } from "./utils/api";
import { useReadAloud } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { HighlightedText, Mark } from "./utils/HighlightedText";

interface GetArticleContentResponse {
    title: string;
    tickers: string;
    content: string;
    audio_url: string;
    paragraphs: string[];
    paragraph_marks: Mark[][];
}

const ARTICLE_AUDIO_SPEED_CACHE_KEY = "articleAudioPlaybackRate";

function loadCachedPlaybackRate(): number {
  try {
    const cached = localStorage.getItem(ARTICLE_AUDIO_SPEED_CACHE_KEY);
    if (cached) {
      const parsed = parseFloat(cached);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  } catch (_) {
    // if localStorage is unavailable, fall back to default
  }
  return 1.5;
}

export const ArticleViewer = ({
  url,
  setActiveArticleUrl,
  contentQuery = "",
}: {
  url: string;
  setActiveArticleUrl: (url: string) => void;
  contentQuery?: string;
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [article, setArticle] = React.useState<GetArticleContentResponse | null>(null);
  const [error, setError] = React.useState<string>("");
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [playbackRate, setPlaybackRate] = React.useState(loadCachedPlaybackRate);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const { voiceControls, setVoiceControls, toggleReading, skipItem } = useReadAloud();

  React.useEffect(() => {
    if (url !== "") fetchArticle();
  }, [url]);

  // Auto-play when audio_url becomes available
  React.useEffect(() => {
    if (article?.audio_url && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [article?.audio_url]);

  // Sync playback rate to audio element when it changes, and remember the choice
  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
    try {
      localStorage.setItem(ARTICLE_AUDIO_SPEED_CACHE_KEY, String(playbackRate));
    } catch (_) {
      // if localStorage is unavailable, silently skip persisting
    }
  }, [playbackRate]);

  // Media key bindings: Previous Track = -10s, Next Track = +10s
  React.useEffect(() => {
    if (!article?.audio_url) return;

    const handler = (e: MediaSessionActionDetails) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (e.action === "previoustrack") {
        audio.currentTime = Math.max(0, audio.currentTime - 10);
      } else if (e.action === "nexttrack") {
        audio.currentTime = Math.min(audio.duration, audio.currentTime + 10);
      }
    };

    navigator.mediaSession.setActionHandler("previoustrack", handler);
    navigator.mediaSession.setActionHandler("nexttrack", handler);

    return () => {
      navigator.mediaSession.setActionHandler("previoustrack", null);
      navigator.mediaSession.setActionHandler("nexttrack", null);
    };
  }, [article?.audio_url]);

  const fetchArticle = async () => {
    setIsLoading(true);
    setArticle(null);
    api
      .get<GetArticleContentResponse>("/article-content", { params: { url, content_query: contentQuery } })
      .then((response) => {
        setArticle(response.data);
        setError("");
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          error.message ||
          "Error fetching article";
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const readableItems = React.useMemo(() => {
    if (!article?.paragraphs) return [];
    const paras = article.paragraphs.filter((p) => p.trim() !== "");
    return [
      { key: "title", texts: [article.title] },
      ...paras.map((p, i) => ({ key: `p-${i}`, texts: [p] })),
    ];
  }, [article]);

  const formatTime = (s: number) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const seek = (delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + delta));
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  return (
    <div className="flex flex-col absolute inset-0 z-10 bg-gray-50 dark:bg-neutral-900">
      {/* Header: close button + custom audio player */}
      <div className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 shrink-0 border-b border-gray-200 dark:border-neutral-700">
        <button
          type="button"
          className="btn btn-default btn-sm shrink-0"
          onClick={(e) => {
            e.preventDefault();
            setActiveArticleUrl("");
          }}
        >
          Close
        </button>

        {article?.audio_url && (
          <>
            <audio
              ref={audioRef}
              src={article.audio_url}
              onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
              onLoadedMetadata={() => {
                setDuration(audioRef.current?.duration ?? 0);
                if (audioRef.current) audioRef.current.playbackRate = playbackRate;
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />

            {/* Play / Pause */}
            <button
              type="button"
              className="btn btn-primary btn-sm shrink-0 w-16"
              onClick={togglePlay}
            >
              {isPlaying ? "⏸" : "▶"}
            </button>

            {/* Speed selector */}
            <select
              title="Playback speed"
              className="text-xs border border-gray-300 dark:border-neutral-600 rounded px-1 py-0.5 bg-white dark:bg-neutral-800 shrink-0"
              value={playbackRate}
              onChange={(e) => setPlaybackRate(Number(e.target.value))}
            >
              <option value={0.75}>0.75×</option>
              <option value={1}>1×</option>
              <option value={1.25}>1.25×</option>
              <option value={1.5}>1.5×</option>
              <option value={1.75}>1.75×</option>
              <option value={2}>2×</option>
              <option value={2.5}>2.5×</option>
            </select>

            {/* Progress bar + time — wraps to its own row on narrow screens */}
            <span className="text-xs tabular-nums shrink-0 text-gray-500 dark:text-gray-400">
              {formatTime(currentTime)}
            </span>
            <input
              type="range"
              title="Seek"
              className="flex-1 min-w-[120px] accent-blue-600"
              min={0}
              max={duration || 0}
              step={1}
              value={currentTime}
              onChange={(e) => {
                const t = Number(e.target.value);
                if (audioRef.current) audioRef.current.currentTime = t;
                setCurrentTime(t);
              }}
            />
            <span className="text-xs tabular-nums shrink-0 text-gray-500 dark:text-gray-400">
              {formatTime(duration)}
            </span>
          </>
        )}
      </div>

      {/* Scrollable article content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading && <div className="text-center">Loading article...</div>}
        {error !== "" && <div className="text-center text-red-500">{error}</div>}

        <article className="prose prose-lg max-w-prose leading-relaxed font-sans">
          <h1> <mark className="bg-red-300">{article?.tickers}</mark> {article?.title}</h1>

          {(article?.paragraphs ?? []).map((paragraph: string, index: number) => (
            <p key={index} id={`p-${index}`} className="mb-4">
              <HighlightedText text={paragraph} marks={article?.paragraph_marks?.[index]} />
            </p>
          ))}
        </article>
      </div>

      {/* ReadAloud bar pinned to bottom */}
      {!article?.audio_url && readableItems.length > 0 && (
        <ReadAloudBar
          voiceControls={voiceControls}
          setVoiceControls={setVoiceControls}
          toggleReading={toggleReading}
          skipItem={skipItem}
          items={readableItems}
          idPrefix="article-"
        />
      )}
    </div>
  );
};
